import { alpha, Tooltip } from "@mui/material";
import {
  buildEnvelopeAreaPath,
  CurvePoint,
  pointsToSvgPath,
  sampleBandPointGroups,
} from "./formulaSamplingCalculation";
import { HOVER_HIT_WIDTH, ShootTimeXScale, ShootTimeYScale } from "./layout";
import {
  MAX_SHOOT_TIME_FINE_TUNE_INDEX,
  MIN_SHOOT_TIME_FINE_TUNE_INDEX,
  ShootTimeBand,
} from "portal/utils/shootTime";
import { useTranslation } from "react-i18next";
import React, { FunctionComponent, useMemo } from "react";

/** Tint strength of the envelope fill over the pastel band background. */
const AREA_FILL_OPACITY = 0.18;

/**
 * Range of achievable laser times across the fine tune index, drawn only for
 * 2.5 almanacs. The FTI 5 curve is rendered separately by ShootTimeFormulaLine.
 */
export interface ShootTimeEnvelopeProps {
  bands: readonly ShootTimeBand[];
  maxRadiusMm: number;
  plotWidth: number;
  xScale: ShootTimeXScale;
  yScale: ShootTimeYScale;
}

export const ShootTimeEnvelope: FunctionComponent<ShootTimeEnvelopeProps> = ({
  bands,
  maxRadiusMm,
  plotWidth,
  xScale,
  yScale,
}) => {
  const { t } = useTranslation();

  const { lowerGroups, upperGroups } = useMemo(() => {
    const sample = (fineTuneIndex: number): CurvePoint[][] =>
      sampleBandPointGroups(bands, maxRadiusMm, plotWidth, {
        formulaVersion: "2.5",
        fineTuneIndex,
      });
    return {
      lowerGroups: sample(MIN_SHOOT_TIME_FINE_TUNE_INDEX),
      upperGroups: sample(MAX_SHOOT_TIME_FINE_TUNE_INDEX),
    };
  }, [bands, maxRadiusMm, plotWidth]);

  const getBandKey = (band: ShootTimeBand): string =>
    `${band.bounds.lower}-${band.bounds.upper}`;

  return (
    <>
      {bands.map((band, bandIndex) => {
        const area = buildEnvelopeAreaPath(
          upperGroups[bandIndex] ?? [],
          lowerGroups[bandIndex] ?? [],
          xScale,
          yScale
        );
        if (!area) {
          return;
        }
        return (
          <path
            key={getBandKey(band)}
            d={area}
            fill={alpha(band.color, AREA_FILL_OPACITY)}
            stroke="none"
            pointerEvents="none"
          />
        );
      })}
      {(
        [
          {
            fineTuneIndex: MAX_SHOOT_TIME_FINE_TUNE_INDEX,
            groups: upperGroups,
          },
          {
            fineTuneIndex: MIN_SHOOT_TIME_FINE_TUNE_INDEX,
            groups: lowerGroups,
          },
        ] as const
      ).map(({ fineTuneIndex, groups }) => (
        <Tooltip
          key={fineTuneIndex}
          arrow
          followCursor
          title={t("views.categoryTemplates.shootTime.fineTuneCurveLabel", {
            value: fineTuneIndex,
          })}
        >
          <g>
            {bands.map((band, bandIndex) => {
              const points = groups[bandIndex];
              if (!points || points.length === 0) {
                return;
              }
              const path = pointsToSvgPath(points, xScale, yScale);
              return (
                <g key={getBandKey(band)}>
                  <path
                    d={path}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={HOVER_HIT_WIDTH}
                    pointerEvents="stroke"
                  />
                  <path
                    d={path}
                    fill="none"
                    stroke={band.color}
                    strokeWidth={1}
                    strokeDasharray="4 4"
                    pointerEvents="none"
                  />
                </g>
              );
            })}
          </g>
        </Tooltip>
      ))}
    </>
  );
};
